import { createContext, ReactNode, useEffect, useReducer, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import { taskAssignsApi } from '../../../axios';
import { mobileUsersApi } from '../../../axios/mobileUsers';
import { useFeatureFlags, useLanguage } from '../../../hooks';
import { useUserDataStore } from '../../../newStore/userDataStore';
import { CreateTaskAssignBody, NameAndIdElement, NameAndIdElementTuple } from '../../../types';
import { paths } from '../../../utils/navigation';
import {
  mapScheduledSendToOption,
  trackTaskCreateFormOpened,
  trackTaskCreated,
  trackTaskDetailViewed,
  trackTaskEdited,
} from '../taskAssignerAnalytics';
import { formatNotifications } from '../utils/editTaskHelpers';
import { createNotifString } from './addnotification';
import {
  initialState,
  TaskAssigned,
  TaskAssignerActions,
  TaskAssignerReducer,
} from './TaskAssignerReducer';

interface ImportAuditPrefillState {
  importAudit?: {
    auditLogId: number;
    entryId: number;
    rowNumber?: number;
  };
  rowData?: Record<string, string | number | null | undefined>;
}

function parseImportDate(raw: unknown): Date | null {
  if (raw == null || raw === '') return null;
  const str = String(raw).trim();
  // Try DD/MM/YYYY HH:mm first (the format used by the import template)
  const m = moment(str, ['DD/MM/YYYY HH:mm', 'DD/MM/YYYY', moment.ISO_8601], true);
  if (m.isValid()) return m.toDate();
  const fallback = new Date(str);
  return isNaN(fallback.getTime()) ? null : fallback;
}

export const TaskAssignerContext = createContext<{
  task: TaskAssigned;
  dispatchTask: React.Dispatch<TaskAssignerActions>;
  formsList: NameAndIdElement[];
  saveTask: () => void;
  goBack: () => void;
  isSubmitting: boolean;
  confirmationModalOpen: boolean;
  setConfirmationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editTask: () => void;
  editMode: boolean;
  // True once initial state is ready to consume. In create-mode it starts
  // true. In edit-mode it flips once `task_data_loaded` hydrates the form
  // (proxied by `task.forms.missing` going false). Consumers that need to
  // wait for hydration before applying defaults can gate on this latch.
  editDataLoaded: boolean;
  disableSaveButton?: boolean;
  accountId?: number;
  initialScheduledSendDaysBefore: number | null;
}>(null);

export const TaskAssignerProvider = ({ children }: { children: ReactNode }) => {
  const lang = useLanguage();

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { taskId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [formsList, setFormsList] = useState<NameAndIdElement[]>([]);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [task, dispatchTask] = useReducer(TaskAssignerReducer, initialState);
  const [initialScheduledSendDaysBefore, setInitialScheduledSendDaysBefore] = useState<number | null>(null);
  const { checkFlag } = useFeatureFlags();
  const { currentUser } = useUserDataStore();
  const accountId = currentUser?.company_id;

  // When navigated here from the import audit "Fix & Retry" button, hydrate
  // scalar fields from the failed row and capture the entry id so we can
  // mark it as resolved after a successful save.
  const prefillState = (location.state ?? {}) as ImportAuditPrefillState;
  const importAuditMeta = prefillState.importAudit;

  const refreshMetadataList = async () => {
    const { forms } = await taskAssignsApi.getLightFilterMetadata();
    const mapEntries = (entry: NameAndIdElementTuple) => ({
      label: entry[0],
      id: entry[1],
    });

    const mappedForms = forms.map((entry) => mapEntries(entry));
    setFormsList(mappedForms);
    return mappedForms;
  };

  const hydrateFromImportAudit = async (
    rowData: ImportAuditPrefillState['rowData'],
    forms: NameAndIdElement[],
  ) => {
    if (!rowData) return;

    const matchedForm = rowData.task
      ? forms.find((f) => f.label === String(rowData.task))
      : undefined;

    // The import worker resolves the user by MobileUser.identifier (typically
    // the email). The form's user picker uses /filter_metadata which only
    // exposes the display name -- not enough to match an emailed identifier.
    // Reuse the same activated-users endpoint (id + name + email) the rest
    // of the app already calls for assignee/notification pickers and match
    // the row's user column against email first, then name as a fallback.
    const rawUser = rowData.user == null ? '' : String(rowData.user).trim();
    const normalizedUser = rawUser.toLowerCase();
    let matchedUser: { id: number } | undefined;
    if (rawUser) {
      try {
        const activatedUsers = await mobileUsersApi.getAllActivatedMobileUsers();
        matchedUser =
          activatedUsers.find((u) => u.email?.trim().toLowerCase() === normalizedUser) ??
          activatedUsers.find((u) => u.name?.trim().toLowerCase() === normalizedUser);
      } catch (_error) {
        // Non-fatal: leave the user field empty so the user can pick manually.
      }
    }

    const parsedDate = parseImportDate(rowData.date);
    const gapNum = Number(rowData.gap);
    const mandatoryNum = Number(rowData.mandatory);
    const lateAllowedRaw = rowData.late_response_allowed;
    const lateAllowed = lateAllowedRaw === 1 || lateAllowedRaw === '1' || lateAllowedRaw === 'true';

    dispatchTask({
      type: 'task_data_loaded',
      payload: {
        ...initialState,
        forms: matchedForm
          ? { value: [matchedForm.id], showError: false, missing: false }
          : initialState.forms,
        users: matchedUser
          ? { value: [matchedUser.id], showError: false, missing: false }
          : initialState.users,
        location: initialState.location,
        locationType: {
          value: rowData.location_type === 'NestableLocation' ? 'NestableLocation' : 'Location',
          showError: false,
          missing: false,
        },
        description: {
          value: rowData.description ? String(rowData.description) : '',
          showError: false,
          missing: false,
        },
        gap: {
          value: Number.isFinite(gapNum) && gapNum > 0 ? gapNum : initialState.gap.value,
          showError: false,
          missing: false,
        },
        subTasks: { value: [], showError: false, missing: false },
        dates: parsedDate
          ? { value: [parsedDate], showError: false, missing: false }
          : initialState.dates,
        hour: parsedDate
          ? { value: parsedDate, showError: false, missing: false }
          : initialState.hour,
        notifications: { value: [], showError: false, missing: false },
        required: {
          value: (mandatoryNum === 0 || mandatoryNum === 2 ? mandatoryNum : 2) as 0 | 2,
          showError: false,
          missing: false,
        },
        canSubmitLate: { value: lateAllowed, showError: false, missing: false },
        taskRecurrence: initialState.taskRecurrence,
      },
    });
  };

  function parseDateKeepingTimezone(dateString: string) {
    const dateWithoutTimezone = moment
      .utc(dateString.substring(0, 23))
      .format('M/DD/yy, hh:mm:ss a')
      .toUpperCase();
    return new Date(dateWithoutTimezone);
  }

  const refreshEditInfo = async () => {
    try {
      const data = await taskAssignsApi.getEditInfo(parseInt(taskId));
      dispatchTask({
        type: 'task_data_loaded',
        payload: {
          users: { value: [data.task.mobile_user_id], showError: false, missing: false },
          forms: { value: [data.task.form_id], showError: false, missing: false },
          location: { value: data.task.location_id, showError: false, missing: false },
          locationType: {
            value: data.task.location_type || 'Location',
            showError: false,
            missing: false,
          },
          description: { value: data.task.description, showError: false, missing: false },
          gap: { value: data.task.gap, showError: false, missing: false },
          subTasks: {
            value: data.subtasks.map((subtask: any) => subtask.name),
            showError: false,
            missing: false,
          },
          dates: {
            value: [new Date(data.task.start_time)],
            showError: false,
            missing: false,
          },
          hour: {
            value: parseDateKeepingTimezone(data.task.start_time),
            showError: false,
            missing: false,
          },
          notifications: {
            value: data.task.notifications ? formatNotifications(data.task.notifications) : [],
            showError: false,
            missing: false,
          },
          required: {
            value: data.task.mandatory === 'for_everyone' ? 2 : 0,
            showError: false,
            missing: false,
          },
          canSubmitLate: {
            value: data.task.late_response_allowed,
            showError: false,
            missing: false,
          },
          asset: {
            value: data.asset_id ? { id: data.asset_id, name: data.asset_name || '' } : null,
            showError: false,
            missing: false,
          },
          scheduledSendDaysBefore: {
            value: data.task.scheduled_send_days_before ?? null,
            showError: false,
            missing: false,
          },
        },
      });
      setInitialScheduledSendDaysBefore(data.task.scheduled_send_days_before ?? null);

      if (accountId) {
        trackTaskDetailViewed({
          accountId,
          taskStatus: data.task.acceptance_status ?? 'sent',
          scheduledSendDaysBefore: data.task.scheduled_send_days_before ?? null,
        });
      }
    } catch (error) {
      navigate(paths.taskAssigner.index);
      enqueueSnackbar(lang.task_assigner.task_does_not_exist, {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    (async () => {
      const forms = await refreshMetadataList();
      if (taskId) {
        refreshEditInfo();
      } else if (prefillState.rowData) {
        await hydrateFromImportAudit(prefillState.rowData, forms);
      }
    })();
  }, []);

  // Amplitude: emit `Task Create Form Opened` once per mount in create mode.
  useEffect(() => {
    if (taskId) return;
    if (!accountId) return;
    trackTaskCreateFormOpened({ accountId, userId: currentUser?.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, taskId]);

  const goBack = () => {
    if (importAuditMeta) {
      // Return to the audit log detail so the user sees the resolved entry.
      return navigate(
        paths.auditLogs.taskImportsDetail.replace(':auditId', importAuditMeta.auditLogId.toString()),
      );
    }
    const filters = searchParams.get('filters');
    const page = searchParams.get('page');
    if (filters || page) {
      const redirectParams = new URLSearchParams({ filters, page }).toString();
      return navigate(`${paths.taskAssigner.index}?${redirectParams}`);
    }
    return navigate(paths.taskAssigner.index);
  };

  const parseTask = (task: TaskAssigned): CreateTaskAssignBody => {
    const formattedTask: CreateTaskAssignBody['task_assign'] = {
      location_id: task.location.value,
      location_type: task.locationType?.value,
      start_time: task.dates.value.map((date) => format(date, 'dd/MM/yyyy')).join(','),
      description: task.description.value,
      gap: task.gap.value.toString(),
      form_id: task.forms.value[0],
      mobile_user_id: task.users.value[0],
      late_response_allowed: task.canSubmitLate.value,
      mandatory: task.required.value,
      scheduled_send_days_before: checkFlag('ScheduledTaskSend')
        ? task.scheduledSendDaysBefore?.value ?? null
        : undefined,
      notifications:
        task.notifications.value.length > 0 ? createNotifString(task.notifications.value) : null,
      asset_id: task.asset?.value?.id ?? null,
    };
    const formattedRecurrence: CreateTaskAssignBody['task_recurrence'] = {
      frequency_unit: task.taskRecurrence?.value?.frequency_unit || 'none',
      days_of_week: task.taskRecurrence?.value?.days_of_week || [],
      minute: task.taskRecurrence?.value?.minute || 0,
      hour: task.taskRecurrence?.value?.hour || 0,
      day_of_month: task.taskRecurrence?.value?.day_of_month || 0,
      month: task.taskRecurrence?.value?.month || 0,
      frequency_value: task.taskRecurrence?.value?.frequency_value || 0,
      end_type: task.taskRecurrence?.value?.end_type || 'never',
      end_date: task.taskRecurrence?.value?.end_date || '',
      end_count: task.taskRecurrence?.value?.end_count || 0,
    };

    return {
      task_assign: formattedTask,
      task_recurrence: checkFlag('TaskRecurrence') ? formattedRecurrence : null,
      mobile_users_ids: task.users.value,
      task_forms_ids: task.forms.value,
      'todo-value': task.subTasks.value.join(','),
      time: {
        date: task.hour.value.getHours() + ':' + task.hour.value.getMinutes(),
      },
    };
  };

  const saveTask = async () => {
    setIsSubmitting(true);
    try {
      const parsedTask = parseTask(task);
      const createResponse = await taskAssignsApi.createTaskAssign(parsedTask);

      if (importAuditMeta) {
        // Best-effort: tell the audit log this entry was fixed manually. We
        // don't fail the whole flow if this call errors out; the TaskAssign
        // was already created.
        try {
          await taskAssignsApi.resolveImportAuditEntry(
            importAuditMeta.auditLogId,
            importAuditMeta.entryId,
            createResponse?.task_assign_id,
          );
          queryClient.invalidateQueries({
            queryKey: ['importAudit', importAuditMeta.auditLogId],
          });
        } catch (resolveError) {
          // Log only; the user already saw the success message for the
          // TaskAssign creation.
          // eslint-disable-next-line no-console
          console.warn('Failed to mark import audit entry as resolved', resolveError);
        }
      }

      if (accountId) {
        const daysBefore = task.scheduledSendDaysBefore?.value ?? null;
        const participationType = task.required?.value === 2 ? 'mandatory' : 'voluntary';
        const hasReminders = task.notifications.value.length > 0;
        const timeToCompleteMinutes = (task.gap.value ?? 0) * 60;
        trackTaskCreated({
          accountId,
          userId: currentUser?.id,
          scheduledSendEnabled: checkFlag('ScheduledTaskSend'),
          scheduledSendOption: mapScheduledSendToOption(daysBefore),
          scheduledSendDaysBefore: daysBefore,
          participationType,
          hasReminders,
          timeToCompleteMinutes,
        });
      }
      setConfirmationModalOpen(false);
      enqueueSnackbar(lang.task_assigner.success_message, {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' },
        autoHideDuration: 6000,
      });
      setTimeout(() => {
        goBack();
      }, 300);
    } catch (showError) {
      setIsSubmitting(false);
      enqueueSnackbar(lang.task_assigner.our_problem, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    }
  };

  const editTask = async () => {
    setIsSubmitting(true);
    try {
      const parsedTask = parseTask(task);
      await taskAssignsApi.updateTaskAssign(parseInt(taskId), parsedTask);
      if (accountId) {
        const daysBefore = task.scheduledSendDaysBefore?.value ?? null;
        const changed = daysBefore !== initialScheduledSendDaysBefore;
        trackTaskEdited({
          accountId,
          scheduledSendChanged: changed,
          scheduledSendOptionBefore: changed ? mapScheduledSendToOption(initialScheduledSendDaysBefore) : null,
          scheduledSendOptionAfter: mapScheduledSendToOption(daysBefore),
        });
      }
      setConfirmationModalOpen(false);
      enqueueSnackbar(lang.task_assigner.update_success_message, {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' },
        autoHideDuration: 4000,
      });
      setTimeout(() => {
        goBack();
      }, 300);
    } catch (showError) {
      setIsSubmitting(false);
      enqueueSnackbar(lang.task_assigner.our_problem, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    }
  };

  const disableSaveButton = Object.values(task).some((value) => value.missing);
  const editMode = Boolean(taskId);
  // Latch that flips once edit data has hydrated. Stays true after that —
  // children must not bounce back to "loading" if the user clears a field
  // (e.g. emptying the forms selection in the drawer) after first paint.
  const [editDataLoaded, setEditDataLoaded] = useState(!editMode);
  useEffect(() => {
    if (editMode && !task?.forms?.missing) setEditDataLoaded(true);
  }, [editMode, task?.forms?.missing]);

  return (
    <TaskAssignerContext.Provider
      value={{
        task,
        dispatchTask,
        formsList,
        saveTask,
        goBack,
        isSubmitting,
        confirmationModalOpen,
        setConfirmationModalOpen,
        editTask,
        editMode,
        editDataLoaded,
        disableSaveButton,
        accountId,
        initialScheduledSendDaysBefore,
      }}
    >
      {children}
    </TaskAssignerContext.Provider>
  );
};
